if (!customElements.get('bst-sticky-add-to-cart')) {
  customElements.define(
    'bst-sticky-add-to-cart',
    class BstStickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = '';
        this.productInfo = null;
        this.variantSelects = null;
        this.mainQuantity = null;
        this.variants = [];
        this.visibilityObserver = null;
        this.variantChangeUnsubscriber = null;
        this.isSyncing = false;
        this.mainOutOfView = false;
        this.nearPageEnd = false;
        this.pageEndThreshold = 50;
        this.scrollTick = null;
        this.hideTimeout = null;
        this.showFrame = null;
        this.onScroll = this.onScroll.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);

        if (!this.productInfo) {
          this.hidden = true;
          return;
        }

        this.variantSelects = this.productInfo.querySelector('variant-selects');
        this.mainQuantity = this.productInfo.querySelector(`#Quantity-${this.sectionId}`);
        this.mainPrice = this.productInfo.querySelector(`#price-${this.sectionId}`);
        this.variants = this.loadVariants();

        this.bindEvents();
        this.initVisibility();
        this.syncVariantSelectsFromMain();
        this.syncQuantityFromMain();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.handleVariantChange(event.data);
        });
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.scrollTick) cancelAnimationFrame(this.scrollTick);
        if (this.showFrame) cancelAnimationFrame(this.showFrame);
        clearTimeout(this.hideTimeout);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.body.classList.remove('bst-sticky-atc-sheet-open');
      }

      loadVariants() {
        const dataEl = this.querySelector('[data-bst-sticky-variants]');
        if (!dataEl) return [];

        try {
          return JSON.parse(dataEl.textContent);
        } catch (error) {
          return [];
        }
      }

      bindEvents() {
        this.querySelector('[data-bst-sticky-variant]')?.addEventListener('change', (event) => {
          this.onVariantSelectChange(event.target, false);
        });

        this.querySelector('[data-bst-sticky-sheet-variant]')?.addEventListener('change', (event) => {
          this.onVariantSelectChange(event.target, true);
        });

        this.querySelector('[data-bst-sticky-quantity]')?.addEventListener('change', () => {
          this.syncQuantityToMain(this.getStickyQuantity());
        });

        this.querySelector('[data-bst-sticky-sheet-quantity]')?.addEventListener('change', () => {
          this.syncQuantityToMain(this.getSheetQuantity());
        });

        this.mainQuantity?.addEventListener('change', () => {
          if (this.isSyncing) return;
          this.syncQuantityFromMain();
        });

        this.querySelector('[data-bst-sticky-submit-desktop]')?.addEventListener('click', () => {
          this.submitAddToCart();
        });

        this.querySelector('[data-bst-sticky-submit-sheet]')?.addEventListener('click', () => {
          this.syncQuantityToMain(this.getSheetQuantity());
          this.submitAddToCart(true);
        });

        this.querySelector('[data-bst-sticky-open-sheet]')?.addEventListener('click', () => {
          this.openSheet();
        });

        this.querySelectorAll('[data-bst-sticky-close-sheet]').forEach((button) => {
          button.addEventListener('click', () => this.closeSheet());
        });
      }

      initVisibility() {
        const trigger =
          this.productInfo.querySelector('.product-form__buttons') ||
          this.productInfo.querySelector('#ProductInfo-' + this.sectionId);

        if (!trigger || !('IntersectionObserver' in window)) {
          this.hidden = false;
          this.classList.add('is-visible');
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.mainOutOfView = !entry.isIntersecting;
            this.updateStickyVisibility();
          },
          { threshold: 0, rootMargin: '0px' }
        );

        this.visibilityObserver.observe(trigger);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      onScroll() {
        if (this.scrollTick) return;

        this.scrollTick = requestAnimationFrame(() => {
          this.scrollTick = null;
          const scrollBottom = window.scrollY + window.innerHeight;
          const pageBottom = document.documentElement.scrollHeight;
          this.nearPageEnd = scrollBottom >= pageBottom - this.pageEndThreshold;
          this.updateStickyVisibility();
        });
      }

      updateStickyVisibility() {
        const show = this.mainOutOfView && !this.nearPageEnd;

        clearTimeout(this.hideTimeout);
        if (this.showFrame) {
          cancelAnimationFrame(this.showFrame);
          this.showFrame = null;
        }

        if (show) {
          this.hidden = false;
          this.showFrame = requestAnimationFrame(() => {
            this.showFrame = requestAnimationFrame(() => {
              this.showFrame = null;
              this.classList.add('is-visible');
            });
          });
          return;
        }

        this.classList.remove('is-visible');
        this.closeSheet();

        this.hideTimeout = setTimeout(() => {
          if (!this.classList.contains('is-visible')) {
            this.hidden = true;
          }
        }, this.getHideDelay());
      }

      getHideDelay() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

        const bar = this.querySelector('.bst-sticky-atc__bar');
        if (!bar || typeof window.getComputedStyle !== 'function') return 320;

        const duration = window.getComputedStyle(bar).transitionDuration;
        const seconds = duration
          .split(',')
          .map((value) => parseFloat(value) || 0)
          .reduce((max, value) => Math.max(max, value), 0);

        return Math.round(seconds * 1000);
      }

      onVariantSelectChange(select, fromSheet) {
        if (this.isSyncing) return;

        const variantId = parseInt(select.value, 10);
        const variant = this.variants.find((item) => item.id === variantId);
        if (!variant) return;

        this.applyVariantToMain(variant);

        this.isSyncing = true;
        const desktopSelect = this.querySelector('[data-bst-sticky-variant]');
        const sheetSelect = this.querySelector('[data-bst-sticky-sheet-variant]');
        if (desktopSelect) desktopSelect.value = String(variantId);
        if (sheetSelect) sheetSelect.value = String(variantId);
        this.isSyncing = false;
      }

      applyVariantToMain(variant) {
        if (!variant?.id) return;

        this.variantSelects = this.productInfo?.querySelector('variant-selects');

        if (this.variantSelects && variant.options?.length) {
          this.syncMainOptionUI(variant);
        }

        this.requestMainVariantUpdate(variant);
        this.updateBrowserUrl(variant.id);
      }

      syncMainOptionUI(variant) {
        const optionGroups = Array.from(
          this.variantSelects.querySelectorAll(
            ':scope > fieldset.product-form__input, :scope > .product-form__input--dropdown'
          )
        );

        variant.options.forEach((value, index) => {
          const group = optionGroups[index];
          if (!group) return;

          const input = group.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);

          if (input) {
            if (!input.checked) {
              group.querySelectorAll('input[type="radio"]').forEach((radio) => {
                radio.checked = false;
              });
              input.checked = true;
            }
            this.variantSelects.updateSelectionMetadata({ target: input });
            return;
          }

          const select = group.querySelector('select');
          if (!select) return;

          const option = Array.from(select.options).find((item) => item.value === value);
          if (!option) return;

          if (select.value !== value) {
            select.value = value;
          }

          Array.from(select.options).forEach((item) => item.removeAttribute('selected'));
          option.setAttribute('selected', 'selected');
          this.variantSelects.updateSelectionMetadata({ target: select });
        });
      }

      requestMainVariantUpdate(variant) {
        const productInfo = this.productInfo;
        if (!productInfo?.dataset?.url || !variant?.id) return;
        if (typeof productInfo.renderProductInfo !== 'function') return;

        const requestUrl = `${productInfo.dataset.url}?variant=${variant.id}&section_id=${productInfo.sectionId}`;

        productInfo.renderProductInfo({
          requestUrl,
          targetId: '',
          preventScroll: true,
          callback: productInfo.handleUpdateProductInfo(productInfo.dataset.url),
        });
      }

      updateBrowserUrl(variantId) {
        const productInfo = this.productInfo;
        if (!productInfo || productInfo.dataset.updateUrl === 'false') return;

        if (typeof productInfo.updateURL === 'function') {
          productInfo.updateURL(productInfo.dataset.url, variantId);
        }
      }

      syncVariantSelectsFromMain() {
        const variantId = this.getMainVariantId();
        if (!variantId) return;

        this.isSyncing = true;
        this.querySelectorAll('[data-bst-sticky-variant], [data-bst-sticky-sheet-variant]').forEach((select) => {
          select.value = String(variantId);
        });
        this.isSyncing = false;
      }

      getMainVariantId() {
        const input = this.productInfo.querySelector(
          `#product-form-${this.sectionId} input[name="id"], #product-form-${this.sectionId} .product-variant-id`
        );
        return input ? parseInt(input.value, 10) : null;
      }

      syncQuantityFromMain() {
        if (!this.mainQuantity) return;
        const value = this.mainQuantity.value;
        this.isSyncing = true;

        const stickyQty = this.querySelector('[data-bst-sticky-quantity]');
        const sheetQty = this.querySelector('[data-bst-sticky-sheet-quantity]');
        if (stickyQty) stickyQty.value = value;
        if (sheetQty) sheetQty.value = value;

        this.isSyncing = false;
      }

      syncQuantityToMain(value) {
        if (!this.mainQuantity || this.mainQuantity.value === value) return;
        this.mainQuantity.value = value;
        this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncQuantityFromMain();
      }

      getStickyQuantity() {
        return this.querySelector('[data-bst-sticky-quantity]')?.value || '1';
      }

      getSheetQuantity() {
        return this.querySelector('[data-bst-sticky-sheet-quantity]')?.value || this.getStickyQuantity();
      }

      handleVariantChange({ html, variant }) {
        if (this.productInfo) {
          this.variantSelects = this.productInfo.querySelector('variant-selects');
        }

        if (this.mainPrice) {
          const sourcePrice = html.getElementById(`price-${this.sectionId}`);
          const destinations = [
            this.querySelector('[data-bst-sticky-price]'),
            this.querySelector('[data-bst-sticky-sheet-price]'),
          ];

          if (sourcePrice) {
            destinations.forEach((destination) => {
              if (destination) destination.innerHTML = sourcePrice.innerHTML;
            });
          }
        }

        this.updateMedia(variant);
        this.updateSubmitState(variant, html);
        this.syncVariantSelectsFromMain();
        this.syncQuantityFromMain();
      }

      updateMedia(variant) {
        const preview = variant?.featured_media?.preview_image;
        const src = preview?.src || variant?.featured_media?.src;
        if (!src) return;

        this.querySelectorAll('[data-bst-sticky-image]').forEach((image) => {
          if (image.tagName !== 'IMG') return;
          image.src = this.buildImageUrl(src, 120);
          image.removeAttribute('srcset');
        });

        this.querySelectorAll('[data-bst-sticky-sheet-image]').forEach((image) => {
          if (image.tagName !== 'IMG') return;
          image.src = this.buildImageUrl(src, 600);
          image.removeAttribute('srcset');
        });
      }

      buildImageUrl(src, width) {
        if (!src) return '';
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', String(width));
          return url.toString();
        } catch (error) {
          return src;
        }
      }

      updateSubmitState(variant, html) {
        const mainSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const disabled = mainSubmit?.hasAttribute('disabled') ?? !variant?.available;
        const label = disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;

        this.querySelectorAll(
          '[data-bst-sticky-submit-desktop], [data-bst-sticky-submit-sheet], [data-bst-sticky-open-sheet]'
        ).forEach((button) => {
          button.toggleAttribute('disabled', disabled);
        });

        this.querySelectorAll(
          '[data-bst-sticky-submit-label], [data-bst-sticky-sheet-submit-label], [data-bst-sticky-mobile-trigger-label]'
        ).forEach((labelEl) => {
          labelEl.textContent = label;
        });
      }

      submitAddToCart(closeSheet = false) {
        const qty = closeSheet ? this.getSheetQuantity() : this.getStickyQuantity();
        this.syncQuantityToMain(qty);

        const productForm = this.productInfo.querySelector('product-form');
        const submitButton = productForm?.querySelector('[type="submit"]');

        if (!submitButton || submitButton.hasAttribute('disabled')) return;

        submitButton.click();

        if (closeSheet) this.closeSheet();
      }

      openSheet() {
        const sheet = this.querySelector('[data-bst-sticky-sheet]');
        if (!sheet) return;

        this.syncVariantSelectsFromMain();
        this.syncQuantityFromMain();

        sheet.hidden = false;
        document.body.classList.add('bst-sticky-atc-sheet-open');
        document.addEventListener('keydown', this.onDocumentKeydown);

        const focusTarget =
          this.querySelector('[data-bst-sticky-sheet-variant]') ||
          this.querySelector('[data-bst-sticky-submit-sheet]');
        focusTarget?.focus();
      }

      closeSheet() {
        const sheet = this.querySelector('[data-bst-sticky-sheet]');
        if (!sheet || sheet.hidden) return;

        sheet.hidden = true;
        document.body.classList.remove('bst-sticky-atc-sheet-open');
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.querySelector('[data-bst-sticky-open-sheet]')?.focus();
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') this.closeSheet();
      }
    }
  );
}
